import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import apiService from '../../services/api';
import logo from '../../Assets/logo.png';
import './certificateTemplate.scss';
import BeatLoader from 'react-spinners/BeatLoader';

const CertificateTemplate = () => {
  const { id } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const data = await apiService.getCertificateById(id);
        if (data) {
          setCertificate(data);
        } else {
          setError('Certificate not found');
        }
      } catch (err) {
        console.error('Error fetching certificate:', err);
        setError('Failed to load certificate');
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleSavePDF = async () => {
    if (!certRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 3, // 3× pixel density → sharp text & visible pattern
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
      });

      // A4 landscape: 297 × 210 mm
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });
      const margin = 8;
      const maxW = 297 - margin * 2;
      const maxH = 210 - margin * 2;

      const imgRatio = canvas.width / canvas.height;
      const pageRatio = maxW / maxH;
      const drawW = imgRatio > pageRatio ? maxW : maxH * imgRatio;
      const drawH = imgRatio > pageRatio ? maxW / imgRatio : maxH;

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(
        imgData,
        'JPEG',
        margin + (maxW - drawW) / 2,
        margin + (maxH - drawH) / 2,
        drawW,
        drawH,
      );

      pdf.save(
        `Share_Certificate_${certificate?.certificate_number || 'export'}.pdf`,
      );
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatDateParts = (dateString) => {
    if (!dateString)
      return { day: '....', month: '............', year: '....' };
    const date = new Date(dateString);
    return {
      day: date.getDate().toString(),
      month: date.toLocaleDateString('en-GB', { month: 'long' }),
      year: date.getFullYear().toString(),
    };
  };

  if (loading) {
    return (
      <div className="loaderContainer">
        <BeatLoader color="#7451f8" />
        <p>Loading certificate...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="certificateError">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/certificates" className="backLink">
          ← Back to Certificates
        </Link>
      </div>
    );
  }

  const dateParts = formatDateParts(certificate?.issued_date);
  const isRevoked = certificate?.status === 'revoked';

  return (
    <div className="certificatePage">
      {/* Action bar — hidden during print */}
      <div className="certificateActions no-print">
        <Link to="/certificates" className="backBtn">
          ← Back to Certificates
        </Link>
        <div className="actionGroup">
          {isRevoked && (
            <span className="revokedBadge">⚠ CERTIFICATE REVOKED</span>
          )}
          <button
            onClick={handlePrint}
            className="printBtn"
            disabled={isRevoked}
          >
            🖨️ Print
          </button>
          <button
            onClick={handleSavePDF}
            className="pdfBtn"
            disabled={isRevoked || exporting}
          >
            {exporting ? 'Generating…' : '⬇ Save as PDF'}
          </button>
        </div>
      </div>

      {/* Certificate document — captured by html2canvas */}
      <div
        ref={certRef}
        className={`certificateDocument ${isRevoked ? 'revoked' : ''}`}
      >
        {isRevoked && <div className="revokedWatermark">REVOKED</div>}

        <div className="certFrame">
          {/* Left stub — absolutely positioned, rotated via transform (not writing-mode)
               so html2canvas captures it correctly without overflow artifacts */}
          <div className="certStub">
            <div className="stubWrap">
              <div className="stubBand">
                <span className="stubTitle">RECEIPT</span>
                <span className="stubSep">·</span>
                <span className="stubField">
                  <em>Cert No.</em>
                  <strong>{certificate?.certificate_number}</strong>
                </span>
                <span className="stubSep">·</span>
                <span className="stubField">
                  <em>Shares</em>
                  <strong>
                    {Number(certificate?.number_of_shares || 0).toLocaleString(
                      'en-GH',
                    )}
                  </strong>
                </span>
                <span className="stubSep">·</span>
                <span className="stubField">
                  <em>Class</em>
                  <strong>{certificate?.share_class}</strong>
                </span>
                <span className="stubSep">·</span>
                <span className="stubNote">Return to the Bank's Secretary</span>
              </div>
            </div>
          </div>

          {/* Main certificate body */}
          <div className="certBody">
            {/*
              Security-print layers — all REAL elements (html2canvas does not
              capture ::before/::after pseudo-elements, so every decorative
              layer must be an actual div to survive PDF export).
            */}
            {/* One whisper-quiet engraved ground + one centred ghost mark —
                restraint is the design. Real elements for html2canvas. */}
            {/* Decorative layers all carry .certLayer so the content
                z-index guard can exclude them as a group */}
            <div className="certLayer certSheen" aria-hidden="true" />
            <div className="certLayer certRosettes" aria-hidden="true" />
            <div className="certLayer certGuilloche" aria-hidden="true" />
            {/* Dense field of small repeated marks (Apex-style) */}
            <div className="certLayer certWatermark" aria-hidden="true">
              {Array.from({ length: 104 }).map((_, i) => (
                <span key={i}>KPCB</span>
              ))}
            </div>

            {/* Header — cert number and share count live on the stub, so
                the title block owns the full width */}
            <div className="certHeader">
              <div className="certTitleBlock">
                <img src={logo} alt="KPRB Logo" className="certLogo" />
                <h1 className="certBankName">KWAHU PRASO COMM. BANK LTD</h1>
                <p className="certIncorp">
                  (Incorporated under the Companies Act, 1963 — Act 179)
                </p>
                <p className="certDocTitle">SHARE CERTIFICATE</p>
              </div>
            </div>

            <div className="certDivider" />

            {/* Body text */}
            <div className="certContent">
              <p className="certifyLine">This is to certify that</p>

              <p className="holderName">{certificate?.shareholder_name}</p>
              <p className="holderAddress">
                of {certificate?.shareholder_address || 'N/A'}
              </p>

              <p className="shareStatement">
                is / are the Registered Holder(s) of{' '}
                <span className="emphasis">
                  {Number(certificate?.number_of_shares || 0).toLocaleString(
                    'en-GH',
                  )}
                </span>{' '}
                Fully Paid{' '}
                <span className="emphasis">{certificate?.share_class}</span>{' '}
                Shares of <strong>No Par Value</strong> in the capital of{' '}
                <strong>Kwahu Praso Community Bank Ltd.</strong>, subject to the
                Rules and Regulations of the Bank.
              </p>

              <p className="sealLine">
                Given under the Common Seal of the Bank.
              </p>

              <p className="dateLine">
                This <span className="datePart">{dateParts.day}</span> day of{' '}
                <span className="datePart">{dateParts.month}</span>,{' '}
                <span className="datePart">{dateParts.year}</span>
              </p>
            </div>

            {/* Signatures */}
            <div className="certSigs">
              <div className="sigBlock">
                <div className="sigLine" />
                <span className="sigRole">Board Secretary</span>
              </div>
              <div className="certSeal">
                <div className="sealRing" />
              </div>
              <div className="sigBlock">
                <div className="sigLine" />
                <span className="sigRole">Director</span>
              </div>
            </div>

            <div className="certDivider" />

            {/* Micro-text security line — reads as a rule from arm's length */}
            <p className="certMicrotext" aria-hidden="true">
              {Array.from({ length: 14 })
                .map(
                  () =>
                    `KPRB·${certificate?.certificate_number}·SHARE CERTIFICATE`,
                )
                .join(' ')}
            </p>

            <p className="certNote">
              <strong>Note:</strong> No transfer of any portion of the shares
              comprised in this Certificate will be registered until this
              certificate has been delivered to the Bank's Office.
            </p>

            {/* <div className="certBottomRow">
              <span>No. of Transfer: ............</span>
              <span>Examined by: ............</span>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplate;
